import { Dialog } from "lib/wts.ui.overlays"
import { Client, getCurrentHost } from "lib/wts.client"
import "lib/jquery.qrcode"

declare const jQuery: any

export interface TwoFactorError extends Error {
    twoFactor: true
    twoFactorKind: "enroll" | "required" | "invalid"
}

export function isTwoFactorError(e: any): e is TwoFactorError {
    return !!(e && e.twoFactor)
}

export async function showPrompt2FA(message?: string): Promise<string | undefined> {
    const dialogContent = document.createElement("div")
    dialogContent.className = "totp-dialog-modal_body"
    dialogContent.style.padding = "20px"
    dialogContent.style.minWidth = "320px"
    dialogContent.innerHTML = /*html*/ `
        <div style="font-size:14pt;font-weight:bold;margin-bottom:8px">Verificação em duas etapas</div>
        <div id="totp-msg" style="color:#666;margin-bottom:16px"></div>
        <div style="margin-bottom:8px">Código do autenticador:</div>
        <input id="totp-code" type="text" inputmode="numeric" pattern="[0-9]*"
               maxlength="8"
               autocomplete="one-time-code"
               style="font-family:monospace;font-size:18pt;letter-spacing:6px;width:100%;padding:8px;border:1px solid #ccc;border-radius:4px"
               placeholder="000000"/>
        <div style="display:flex;gap:8px;margin-top:20px;justify-content:flex-end">
            <button id="totp-cancel" class="mln-button">Cancelar</button>
            <button id="totp-ok" class="mln-button" disabled>Entrar</button>
        </div>
    `

    const msgEl = dialogContent.querySelector<HTMLDivElement>("#totp-msg")!
    if (message) msgEl.innerText = message
    else msgEl.innerText = "Abra seu app de autenticação e digite o código de 6 dígitos."

    const dialog = new Dialog()
    const codeEl = dialogContent.querySelector<HTMLInputElement>("#totp-code")!
    const okBtn = dialogContent.querySelector<HTMLButtonElement>("#totp-ok")!
    const cancelBtn = dialogContent.querySelector<HTMLButtonElement>("#totp-cancel")!

    codeEl.oninput = () => { okBtn.disabled = codeEl.value.replace(/\D/g, "").length < 6 }
    codeEl.onkeydown = (e) => { if (e.key === "Enter" && !okBtn.disabled) dialog.close("ok") }
    okBtn.onclick = () => dialog.close("ok")
    cancelBtn.onclick = () => dialog.close("cancel")

    setTimeout(() => codeEl.focus(), 50)

    const result = await dialog.show(dialogContent, {
        className: "totp-dialog-modal",
        lightDismiss: false,
        width: "auto",
        height: "auto",
        minHeight: "fit-content",
        topLayer: true,
    })

    dialogContent.remove()

    return result === "ok" ? codeEl.value.replace(/\D/g, "") : undefined
}

export async function showEnroll2FA(username: string, password: string): Promise<string | undefined> {
    // 1) gera segredo no servidor (autenticando com user+pass)
    let genResult: any
    try {
        genResult = await Client.get<any>("wtssystem.users.2FA.GenerateSecret",
            { username: username.toUpperCase(), password },
            { anonymous: true, neverPost: true })
    } catch (e: any) {
        throw new Error("Falha ao gerar segredo 2FA: " + (e?.message ?? e))
    }

    const row = genResult?.value?.[0] ?? genResult?.[0] ?? genResult
    const secret: string = row?.secret ?? row?.SECRET ?? ""
    const otpauthUrl: string = row?.otpauth_url ?? row?.OTPAUTH_URL ?? ""

    if (!secret) throw new Error("Resposta inválida ao gerar segredo 2FA")

    // 2) mostra diálogo com QR + chave + campo para validar primeiro código
    const dialogContent = document.createElement("div")
    dialogContent.className = "totp-enroll-modal_body"
    dialogContent.style.padding = "20px"
    dialogContent.style.maxWidth = "480px"
    dialogContent.innerHTML = /*html*/ `
        <div style="font-size:14pt;font-weight:bold;margin-bottom:12px">Configurar autenticação em duas etapas</div>
        <div style="color:#666;margin-bottom:16px;font-size:11pt;line-height:1.5">
            Abra um app autenticador (Google Authenticator, Microsoft Authenticator, Authy, 1Password etc.)
            e escaneie o QR Code abaixo, ou digite manualmente a chave.
        </div>
        <div id="totp-qr" style="display:flex;justify-content:center;background:#fff;padding:12px;border-radius:8px;margin-bottom:12px"></div>
        <div style="margin-bottom:6px;font-size:10pt;color:#888">Chave manual:</div>
        <div style="display:flex;gap:8px;margin-bottom:16px">
            <input id="totp-secret" type="text" readonly value="${secret}"
                   style="flex:1;font-family:monospace;font-size:11pt;padding:8px;border:1px solid #ccc;border-radius:4px;background:#f5f5f5"/>
            <button id="totp-copy" class="mln-button" type="button">Copiar</button>
        </div>
        <div style="margin-bottom:8px">Confirme com o código exibido pelo app:</div>
        <input id="totp-code" type="text" inputmode="numeric" pattern="[0-9]*"
               maxlength="8"
               autocomplete="one-time-code"
               style="font-family:monospace;font-size:18pt;letter-spacing:6px;width:100%;padding:8px;border:1px solid #ccc;border-radius:4px"
               placeholder="000000"/>
        <div style="display:flex;gap:8px;margin-top:20px;justify-content:flex-end">
            <button id="totp-cancel" class="mln-button">Cancelar</button>
            <button id="totp-activate" class="mln-button" disabled>Ativar</button>
        </div>
    `

    // renderiza QR via jquery.qrcode
    const qrEl = dialogContent.querySelector<HTMLDivElement>("#totp-qr")!
    try {
        jQuery(qrEl).qrcode({ text: otpauthUrl, width: 200, height: 200, correctLevel: 1 })
    } catch (e) {
        qrEl.innerHTML = '<div style="color:#888">Não foi possível gerar o QR Code. Use a chave manual.</div>'
    }

    const dialog = new Dialog()
    const codeEl = dialogContent.querySelector<HTMLInputElement>("#totp-code")!
    const okBtn = dialogContent.querySelector<HTMLButtonElement>("#totp-activate")!
    const cancelBtn = dialogContent.querySelector<HTMLButtonElement>("#totp-cancel")!
    const copyBtn = dialogContent.querySelector<HTMLButtonElement>("#totp-copy")!

    codeEl.oninput = () => { okBtn.disabled = codeEl.value.replace(/\D/g, "").length < 6 }
    codeEl.onkeydown = (e) => { if (e.key === "Enter" && !okBtn.disabled) dialog.close("ok") }
    okBtn.onclick = () => dialog.close("ok")
    cancelBtn.onclick = () => dialog.close("cancel")
    copyBtn.onclick = () => { navigator.clipboard?.writeText(secret) }

    setTimeout(() => codeEl.focus(), 50)

    const result = await dialog.show(dialogContent, {
        className: "totp-enroll-modal",
        lightDismiss: false,
        width: "auto",
        height: "auto",
        minHeight: "fit-content",
        topLayer: true,
    })

    dialogContent.remove()

    if (result !== "ok") return undefined

    const code = codeEl.value.replace(/\D/g, "")

    // 3) ativa no servidor
    try {
        await Client.get<any>("wtssystem.users.2FA.Activate",
            { username: username.toUpperCase(), password, secret, code },
            { anonymous: true, neverPost: true })
    } catch (e: any) {
        throw new Error("Falha ao ativar 2FA: " + (e?.message ?? e))
    }

    return code
}
